// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Button} from '@app/features/ui/button/Button';
import {PlayIcon, StopIcon, WaveformIcon} from '@app/features/ui/icons/Icons';
import {AudioLevelMeter} from '@app/features/user/components/modals/tabs/components/AudioLevelMeter';
import styles from '@app/features/user/components/modals/tabs/components/MicTestSection.module.css';
import type {MicTestSettings} from '@app/features/user/components/modals/tabs/hooks/useMicTest';
import {MIC_TEST_SAMPLE_SECONDS, useMicTest} from '@app/features/user/components/modals/tabs/hooks/useMicTest';
import NativeCallSession from '@app/features/voice/native/NativeCallSession';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

interface MicTestSectionProps {
	settings: MicTestSettings;
}

const WebMicTestSection: React.FC<MicTestSectionProps> = observer(({settings}) => {
	const {t} = useLingui();
	const {
		error,
		isTesting,
		isStarting,
		level,
		start,
		stop,
		canRecordSample,
		sample,
		isRecordingSample,
		playingSample,
		recordSample,
		playSample,
	} = useMicTest(settings);
	const showSampleRow = canRecordSample && (isTesting || sample !== null);
	const sampleIcon = (kind: 'raw' | 'processed') =>
		playingSample === kind ? (
			<StopIcon size={remFromPx(14)} weight="fill" />
		) : (
			<PlayIcon size={remFromPx(14)} weight="fill" />
		);
	return (
		<div className={styles.container} data-flx="user.mic-test-section.container">
			<div className={styles.bar} data-active={isTesting ? 'true' : 'false'} data-flx="user.mic-test-section.bar">
				<div className={styles.meter} data-flx="user.mic-test-section.meter">
					<AudioLevelMeter level={isTesting ? level : 0} data-flx="user.mic-test-section.audio-level-meter" />
				</div>

				<Button
					variant={isTesting ? 'secondary' : 'primary'}
					className={styles.actionButton}
					onClick={isTesting ? stop : start}
					submitting={isStarting}
					square={true}
					icon={
						isTesting ? (
							<StopIcon size={remFromPx(16)} weight="fill" data-flx="user.mic-test-section.stop-icon" />
						) : (
							<PlayIcon size={remFromPx(16)} weight="fill" data-flx="user.mic-test-section.start-icon" />
						)
					}
					aria-label={isTesting ? t`Stop mic test` : t`Start mic test`}
					data-flx="user.mic-test-section.action-button"
				/>
			</div>
			<p className={styles.samplesHint} role={error ? 'alert' : 'status'}>
				{error === 'permission' ? (
					<Trans>Allow microphone access in your device settings, then try again.</Trans>
				) : error ? (
					<Trans>Could not start the mic test. Check your microphone and try again.</Trans>
				) : isStarting ? (
					<Trans>Starting microphone…</Trans>
				) : isTesting ? (
					<Trans>
						Speak to see the level and hear your voice back after a short delay. Use headphones to avoid feedback.
					</Trans>
				) : (
					<Trans>Start the test, then speak to check your microphone.</Trans>
				)}
			</p>
			{showSampleRow && (
				<div className={styles.samples} data-flx="user.mic-test-section.samples">
					<Button
						variant="secondary"
						small={true}
						leftIcon={<WaveformIcon size={remFromPx(14)} weight="fill" />}
						onClick={() => void recordSample()}
						disabled={!isTesting}
						recording={isRecordingSample}
						submitting={isRecordingSample}
						data-flx="user.mic-test-section.record-sample"
					>
						<Trans>Record {MIC_TEST_SAMPLE_SECONDS}s sample</Trans>
					</Button>
					{sample && (
						<>
							<Button
								variant="secondary"
								small={true}
								leftIcon={sampleIcon('raw')}
								onClick={() => void playSample('raw')}
								data-flx="user.mic-test-section.play-raw"
							>
								<Trans>Original</Trans>
							</Button>
							<Button
								variant="secondary"
								small={true}
								leftIcon={sampleIcon('processed')}
								onClick={() => void playSample('processed')}
								data-flx="user.mic-test-section.play-processed"
							>
								<Trans>Processed</Trans>
							</Button>
						</>
					)}
					<span className={styles.samplesHint} data-flx="user.mic-test-section.samples-hint">
						<Trans>Compare your raw microphone with what others hear after processing.</Trans>
					</span>
				</div>
			)}
		</div>
	);
});

/** Native calls own the microphone. Check their actual speech events without
 * starting a competing WebView capture or changing the phone's audio route. */
const NativeMicTestSection = observer(() => {
	const [testing, setTesting] = useState(false);
	const {state, speakingUserIds} = NativeCallSession;
	const muted = state.muted || state.deafened;
	const speaking = !muted && speakingUserIds.includes(Authentication.currentUserId ?? '');
	return (
		<div className={styles.container}>
			<div className={styles.bar} data-active={testing && speaking ? 'true' : 'false'}>
				<div className={styles.meter}>
					<WaveformIcon size={24} />
					<span role="status">
						{muted ? (
							<Trans>Your microphone is muted</Trans>
						) : testing ? (
							speaking ? (
								<Trans>Speech detected</Trans>
							) : (
								<Trans>Listening for your voice…</Trans>
							)
						) : (
							<Trans>Check your call microphone</Trans>
						)}
					</span>
				</div>
				<Button variant="secondary" onClick={() => setTesting(!testing)} disabled={muted}>
					{testing ? <Trans>Stop</Trans> : <Trans>Test mic</Trans>}
				</Button>
			</div>
			<p className={styles.samplesHint}>
				<Trans>
					Checks speech detection from your active call. Other participants can still hear you. End the call to test
					playback and processing.
				</Trans>
			</p>
			{muted && (
				<Button variant="secondary" onClick={NativeCallSession.openSheet}>
					<Trans>Open call controls to unmute</Trans>
				</Button>
			)}
		</div>
	);
});

export const MicTestSection: React.FC<MicTestSectionProps> = observer(({settings}) =>
	NativeCallSession.active ? <NativeMicTestSection /> : <WebMicTestSection settings={settings} />,
);
